/**
 * Stepping through a list with a thumb.
 *
 * The arrows in a form's header are a mouse's answer. On a phone they are
 * two small targets in the one strip of the dialog that also holds the
 * title, the history button and the way out — and the gesture everybody
 * already makes for "show me the next one" is a sideways flick.
 *
 * The direction follows time, not the list: right brings the YOUNGER
 * transaction, left the older, the same way the pages of a book go. That
 * the log happens to be drawn newest-first is a property of the list, and
 * tying a gesture to a sort order would mean it reversed itself whenever
 * somebody re-sorted.
 */
export interface SwipeNavOptions {
  /** Swiped left — older, further down a newest-first list. */
  onLeft: () => void;
  /** Swiped right — younger, further up it. */
  onRight: () => void;
  /** Horizontal travel, in CSS pixels, before it counts as a swipe at all. */
  threshold?: number;
  /** Longer than this it is a drag with a destination, not a flick. */
  maxMs?: number;
}

/**
 * Controls whose own meaning for a sideways drag outranks ours.
 *
 * `.cf-drag-handle` is what `makeDraggable` marks its handle with, and it is
 * the general form of the rule: an element that already owns a pointer drag
 * keeps it. Both transaction forms are movable by their header, so without
 * this a thumb that pushed the dialog aside would also step to another
 * transaction.
 */
const OWN_GESTURE = 'input,textarea,select,[contenteditable],'
  + '.cf-drag-handle,[data-dlg-handle],[data-no-swipe]';

/**
 * @returns a function that takes the listeners off again. Both forms are
 * static elements re-opened many times per session, so every wiring needs
 * its matching unwiring or the swipes compound.
 */
export function wireSwipeNav(target: HTMLElement, opts: SwipeNavOptions): () => void {
  const threshold = opts.threshold ?? 60;
  const maxMs = opts.maxMs ?? 800;
  let start: { x: number; y: number; at: number; id: number } | undefined;

  const down = (event: PointerEvent): void => {
    start = undefined;
    // Touch only. A mouse has the buttons, and a trackpad's two-finger
    // scroll arrives as a wheel — but a pen resting on a field would
    // otherwise be read as the beginning of a flick.
    if (event.pointerType !== 'touch') return;
    const from = event.target as HTMLElement | null;
    if (from?.closest(OWN_GESTURE)) return;
    if (scrollsSideways(from, target)) return;
    start = { x: event.clientX, y: event.clientY, at: Date.now(), id: event.pointerId };
  };

  const up = (event: PointerEvent): void => {
    const began = start;
    start = undefined;
    if (!began || event.pointerId !== began.id) return;
    if (Date.now() - began.at > maxMs) return;
    const dx = event.clientX - began.x;
    const dy = event.clientY - began.y;
    // Sideways by a clear margin. Without this a scroll that drifts a little
    // left or right would navigate, which on a form is the worst possible
    // surprise: you look away and you are on a different transaction.
    if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 2) return;
    if (dx > 0) opts.onRight(); else opts.onLeft();
  };

  const cancel = (): void => { start = undefined; };

  target.addEventListener('pointerdown', down);
  target.addEventListener('pointerup', up);
  target.addEventListener('pointercancel', cancel);
  return () => {
    target.removeEventListener('pointerdown', down);
    target.removeEventListener('pointerup', up);
    target.removeEventListener('pointercancel', cancel);
  };
}

/**
 * Is the touch starting inside something that scrolls sideways itself?
 *
 * The full editor's split table is wider than a phone, and reading the far
 * column of it is a sideways drag. Taking that drag away to step to another
 * transaction would make the right-hand half of the form unreachable.
 */
function scrollsSideways(from: HTMLElement | null, stopAt: HTMLElement): boolean {
  for (let el = from; el && el !== stopAt.parentElement; el = el.parentElement) {
    if (el.scrollWidth - el.clientWidth <= 1) continue;
    const overflow = getComputedStyle(el).overflowX;
    if (overflow === 'auto' || overflow === 'scroll') return true;
  }
  return false;
}
